import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

interface FieldContextValue {
  id: string;
  helperId: string;
  errorId: string;
  hasError: boolean;
}
const FieldContext = createContext<FieldContextValue | null>(null);

function useFieldContext() {
  return useContext(FieldContext);
}

interface RootProps {
  /** When provided, helper text and error are wired up to the control. */
  children: ReactNode;
  className?: string;
  /** Pass true if this field is in an error state — adds danger styling. */
  error?: boolean;
}

function Root({ children, className, error }: RootProps) {
  const baseId = useId();
  const ctx: FieldContextValue = {
    id: `${baseId}-control`,
    helperId: `${baseId}-helper`,
    errorId: `${baseId}-error`,
    hasError: !!error,
  };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn("flex flex-col gap-1.5", className)}>{children}</div>
    </FieldContext.Provider>
  );
}

interface LabelProps {
  children: ReactNode;
  /** Render the word "optional" in muted next to the label. (DESIGN_GUIDE §4) */
  optional?: boolean;
  className?: string;
}

function Label({ children, optional, className }: LabelProps) {
  const ctx = useFieldContext();
  return (
    <label
      htmlFor={ctx?.id}
      className={cn("flex items-center gap-1.5 text-sm font-medium text-fg", className)}
    >
      {children}
      {optional && (
        <span className="text-xs font-normal text-fg-subtle">(optional)</span>
      )}
    </label>
  );
}

function Helper({ children, className }: { children: ReactNode; className?: string }) {
  const ctx = useFieldContext();
  return (
    <p id={ctx?.helperId} className={cn("text-xs text-fg-muted", className)}>
      {children}
    </p>
  );
}

function Error({ children, className }: { children: ReactNode; className?: string }) {
  const ctx = useFieldContext();
  return (
    <p
      id={ctx?.errorId}
      role="alert"
      className={cn("text-xs text-danger-fg", className)}
    >
      {children}
    </p>
  );
}

/**
 * Wires a control (input, select, etc.) to the surrounding Field.
 * Pass via `<Field.Control as={Input} ... />` or just spread `useFieldControlProps()`.
 */
export function useFieldControlProps(): {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
} {
  const ctx = useFieldContext();
  if (!ctx) return { id: "" };
  return {
    id: ctx.id,
    "aria-describedby": ctx.helperId,
    "aria-invalid": ctx.hasError || undefined,
  };
}

/**
 * Plain text input that auto-wires to the parent Field.
 * Use this *inside* <Field.Root>...</Field.Root>.
 */
const FieldInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  const wired = useFieldControlProps();
  return (
    <input
      ref={ref}
      {...wired}
      className={cn(
        "h-9 w-full rounded-[var(--radius-md)] border border-border-default bg-surface px-3 text-sm text-fg",
        "placeholder:text-fg-subtle",
        "transition-[border-color,box-shadow] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
        "hover:border-border-strong",
        "disabled:cursor-not-allowed disabled:bg-muted disabled:text-fg-disabled",
        "aria-[invalid=true]:border-danger-fg",
        className,
      )}
      {...props}
    />
  );
});
FieldInput.displayName = "Field.Input";

export const Field = {
  Root,
  Label,
  Helper,
  Error,
  Input: FieldInput,
};
